import { Fragment, type ReactNode } from 'react';
import { AssetImage } from '@/components/files/asset-image';
import { cn } from '@/lib/utils';

/**
 * Tiny, safe Markdown-ish renderer (no HTML injection): headings, (nested) lists, code fences,
 * block quotes, GFM tables, paragraphs, and inline `code`, **bold**, *italic*, [links](https://…), [links](/w/…) inside Deskworks
 * and bare https:// addresses (clickable as they are). Pictures ![alt](…) come from the workspace's own files
 * (AssetImage); other addresses stay a placeholder.
 */
export function Markdown({ text, className }: { text: string; className?: string }) {
  return <div className={cn('flex flex-col gap-3 text-sm leading-relaxed break-words', className)}>{renderBlocks(text)}</div>;
}

function renderBlocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      i++;
      continue;
    }
    const fence = /^```(\w*)/.exec(line.trim());
    if (fence) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) buf.push(lines[i++]);
      i++;
      out.push(
        <pre key={key++} className="overflow-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
          {buf.join('\n')}
        </pre>,
      );
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const cls = level <= 1 ? 'text-lg font-semibold' : level === 2 ? 'text-base font-semibold' : 'text-sm font-semibold';
      out.push(
        <div key={key++} className={cn(cls, 'mt-1')}>
          {renderInline(heading[2])}
        </div>,
      );
      i++;
      continue;
    }
    if (LIST_ITEM.test(line)) {
      const [list, next] = parseList(lines, i, -1);
      out.push(<ListBlock key={key++} list={list} />);
      i = next;
      continue;
    }
    if (line.startsWith('>')) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) buf.push(lines[i++].replace(/^>\s?/, ''));
      out.push(
        <blockquote key={key++} className="flex flex-col gap-2 rounded-r-md border-l-2 border-primary/40 bg-muted/40 py-1.5 pr-3 pl-3 text-foreground/80">
          {renderBlocks(buf.join('\n'))}
        </blockquote>,
      );
      continue;
    }
    if (isTableStart(lines, i)) {
      const align = splitRow(lines[i + 1]).map(cellAlign);
      const header = splitRow(lines[i]);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') rows.push(splitRow(lines[i++]));
      out.push(
        <div key={key++} className="overflow-x-auto rounded-lg border">
          <table className="w-full border-collapse text-[0.95em]">
            <thead className="bg-muted/50">
              <tr>
                {header.map((c, idx) => (
                  <th key={idx} className={cn('border-b px-3 py-1.5 font-semibold whitespace-nowrap', alignClass(align[idx]))}>
                    {renderInline(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ridx) => (
                <tr key={ridx} className="border-b last:border-b-0">
                  {header.map((_, idx) => (
                    <td key={idx} className={cn('px-3 py-1.5 align-top', alignClass(align[idx]))}>
                      {renderInline(r[idx] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      out.push(<hr key={key++} className="border-border" />);
      i++;
      continue;
    }
    const buf: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6})\s/.test(lines[i]) &&
      !/^```/.test(lines[i].trim()) &&
      !LIST_ITEM.test(lines[i]) &&
      !lines[i].startsWith('>') &&
      !isTableStart(lines, i)
    ) {
      buf.push(lines[i++]);
    }
    out.push(
      <p key={key++} className="whitespace-pre-wrap">
        {buf.map((l, idx) => (
          <Fragment key={idx}>
            {idx > 0 && '\n'}
            {renderInline(l)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return out;
}

const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;

type List = { ordered: boolean; start: number; items: { text: string; children: List[] }[] };

/**
 * The list from line i on. Items indented deeper than the item before them form a list inside it; an item at most as
 * deep as the parent's (parentIndent) ends this one. A blank line between items keeps the list going.
 */
function parseList(lines: string[], i: number, parentIndent: number): [List, number] {
  const first = LIST_ITEM.exec(lines[i])!;
  const indent = first[1].length;
  const ordered = /\d/.test(first[2]);
  const list: List = { ordered, start: ordered ? parseInt(first[2], 10) : 1, items: [] };
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      let j = i;
      while (j < lines.length && lines[j].trim() === '') j++;
      const after = j < lines.length ? LIST_ITEM.exec(lines[j]) : null;
      if (!after || after[1].length <= parentIndent) break;
      i = j;
    }
    const m = LIST_ITEM.exec(lines[i]);
    if (!m || m[1].length <= parentIndent) break;
    // Bullets after numbers (or the other way round) start a list of their own.
    if (m[1].length <= indent && /\d/.test(m[2]) !== ordered) break;
    const last = list.items.at(-1);
    if (m[1].length > indent && last) {
      const [child, next] = parseList(lines, i, indent);
      last.children.push(child);
      i = next;
      continue;
    }
    const item = { text: m[3], children: [] as List[] };
    i++;
    while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !LIST_ITEM.test(lines[i])) item.text += ' ' + lines[i++].trim();
    list.items.push(item);
  }
  return [list, i];
}

function ListBlock({ list }: { list: List }) {
  const ListTag = list.ordered ? 'ol' : 'ul';
  return (
    <ListTag
      start={list.ordered && list.start !== 1 ? list.start : undefined}
      className={cn('flex flex-col gap-1 pl-5 marker:text-muted-foreground', list.ordered ? 'list-decimal' : 'list-disc')}
    >
      {list.items.map((it, idx) => (
        <li key={idx} className="pl-0.5">
          {renderTaskItem(it.text)}
          {it.children.map((c, cidx) => (
            <div key={cidx} className="mt-1">
              <ListBlock list={c} />
            </div>
          ))}
        </li>
      ))}
    </ListTag>
  );
}

type Align = 'left' | 'center' | 'right' | undefined;

/** A header row followed by a delimiter row like `|---|:---:|`. */
function isTableStart(lines: string[], i: number): boolean {
  if (i + 1 >= lines.length || !lines[i].includes('|') || !lines[i + 1].includes('|')) return false;
  const delim = splitRow(lines[i + 1]);
  return delim.length > 0 && delim.every((c) => /^:?-+:?$/.test(c));
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  return s.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
}

function cellAlign(delim: string): Align {
  const l = delim.startsWith(':');
  const r = delim.endsWith(':');
  return l && r ? 'center' : r ? 'right' : l ? 'left' : undefined;
}

function alignClass(a: Align): string {
  return a === 'center' ? 'text-center' : a === 'right' ? 'text-right' : 'text-left';
}

function renderTaskItem(text: string): ReactNode {
  const task = /^\[( |x|X)\]\s+(.*)$/.exec(text);
  if (!task) return renderInline(text);
  const done = task[1].toLowerCase() === 'x';
  return (
    <span className={cn(done && 'text-muted-foreground line-through')}>
      <span aria-hidden className="mr-1.5">{done ? '☑' : '☐'}</span>
      {renderInline(task[2])}
    </span>
  );
}

// A bare address ends before trailing punctuation and closing brackets (“see https://x.de/a.” → https://x.de/a).
const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\((https?:\/\/[^)\s]+|\/w\/[^)\s]+)\))|(https?:\/\/[^\s<>()[\]`"']*[^\s<>()[\]`"'.,;:!?])|(!\[([^\]]*)\]\(<?([^)\s>]+)>?(?:\s+"[^"]*")?\))/g;

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    if (m[1]) {
      out.push(
        <code key={k++} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (m[2]) {
      out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    } else if (m[3]) {
      out.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    } else if (m[4]) {
      const label = /^\[([^\]]+)\]/.exec(tok)?.[1] ?? tok;
      out.push(
        // A path inside Deskworks (e.g. a view an agent made) opens here; other links open in a new tab.
        <a key={k++} href={m[5]} {...(m[5].startsWith('/') ? {} : { target: '_blank', rel: 'noreferrer noopener' })} className="text-primary underline underline-offset-2">
          {label}
        </a>,
      );
    } else if (m[7]) {
      out.push(<AssetImage key={k++} src={m[9]} alt={m[8]} className="my-1" />);
    } else if (m[6]) {
      out.push(
        <a key={k++} href={tok} target="_blank" rel="noreferrer noopener" className="text-primary underline underline-offset-2 break-all">
          {tok}
        </a>,
      );
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
